/**
 * SettingsPage — Suite Standard (LevelUp Suite parity with BrandTalk).
 *
 * Uses SettingsShell from @levelup/ui-management/settings for tab layout.
 * Uses shared section components (ProfileSection, WorkspaceSection, BillingSection,
 * NotificationPreferencesSection) and MembershipSection from @levelup/ui-management.
 *
 * Central is the SOLE authority for profile, workspace, billing, and membership data.
 * Capability gating via usePermissions — no role/scope fallback.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  SettingsShell,
  ProfileSection,
  WorkspaceSection,
  BillingSection,
  NotificationPreferencesSection,
  SETTINGS_TAB_IDS,
} from '@levelup/ui-management/settings';
import type { SettingsTabConfig } from '@levelup/ui-management/settings';
import type { BillingState } from '@levelup/ui-management/billing';
import { MembershipSection } from '@levelup/ui-management/membership';
import type {
  CentralRole,
  InviteRoleOption,
  MembershipPermissions,
  PendingInvite,
  WorkspaceMember,
} from '@levelup/central-auth';
import type { CategoryPreference } from '@levelup/central-notifications';

import { useBootContext } from '../hooks/useBootContext';
import { usePermissions } from '../hooks/usePermissions';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useBillingProvider } from '../hooks/useBillingProvider';
import { useToast } from '../components/ui/Toast';
import { createPortalSession } from '../lib/centralBilling';
import {
  listMembers,
  listPendingInvites,
  inviteMember,
  updateMemberRole,
  removeMember,
  resendInvite,
  cancelInvite,
} from '../lib/centralMembership';
import { fetchInviteOptions, InviteOptionsError } from '../lib/centralInviteOptions';
import {
  fetchNotificationPreferences,
  updateNotificationCategory,
} from '../lib/centralNotificationPrefs';
import { fetchSwitchContext } from '../lib/centralBootstrap';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { bootSuccess } from '../store/slices/bootSlice';

const CENTRAL_URL = (import.meta.env.VITE_CENTRAL_URL ?? '').replace(/\/+$/, '');

// Role hierarchy used to derive membership permissions (app-local, not shared yet).
const ROLE_RANK: Record<CentralRole, number> = {
  owner: 4,
  admin: 3,
  member: 2,
  viewer: 1,
};
const ALL_ROLES: CentralRole[] = ['viewer', 'member', 'admin', 'owner'];

function rolesSelectableBy(currentRole: CentralRole | undefined): CentralRole[] {
  if (!currentRole) return [];
  const rank = ROLE_RANK[currentRole] ?? 0;
  // A user may invite/assign roles strictly below their own rank.
  return ALL_ROLES.filter((r) => ROLE_RANK[r] < rank);
}

export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const bootContext = useBootContext();
  const { hasWorkspacePermission, hasRole } = usePermissions();

  const user = bootContext?.user ?? null;
  const workspace = bootContext?.currentWorkspace ?? null;

  const tabFromPath = useCallback((pathname: string): string => {
    const segment = pathname.split('/').filter(Boolean).pop();
    if (!segment || segment === 'settings') return SETTINGS_TAB_IDS.PROFILE;
    return segment;
  }, []);

  const [activeTab, setActiveTab] = useState<string>(() => tabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname, tabFromPath]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      const target = tabId === SETTINGS_TAB_IDS.PROFILE ? '/settings' : `/settings/${tabId}`;
      navigate(target, { replace: true });
    },
    [navigate],
  );

  const openCentral = useCallback((path: string) => {
    if (!CENTRAL_URL) return;
    const returnUrl = encodeURIComponent(window.location.href);
    window.open(`${CENTRAL_URL}${path}?returnUrl=${returnUrl}`, '_blank', 'noopener,noreferrer');
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case SETTINGS_TAB_IDS.PROFILE: {
        // Viewers are strictly read-only across all settings surfaces.
        // The shared ProfileSection requires onEditProfile, so we hide the
        // button via CSS (data-readonly attribute) when the role is viewer.
        const canEditProfile = !hasRole('viewer');
        return (
          <div data-readonly={canEditProfile ? 'false' : 'true'} className="lvl-legacy-profile-section">
            <ProfileSection
              avatarUrl={user?.avatarUrl ?? null}
              fullName={user?.fullName ?? user?.name ?? null}
              email={user?.email ?? ''}
              onEditProfile={() => openCentral('/settings/profile')}
            />
          </div>
        );
      }

      case SETTINGS_TAB_IDS.WORKSPACE: {
        // Viewers and non-admin members can't edit workspace settings.
        // The shared WorkspaceSection requires onEditWorkspace; we hide the
        // button via CSS (data-readonly attribute) when the role is read-only.
        const canEditWorkspace = hasRole('owner', 'admin');
        return (
          <div data-readonly={canEditWorkspace ? 'false' : 'true'} className="lvl-legacy-workspace-section">
            <WorkspaceSection
              name={workspace?.name ?? ''}
              slug={workspace?.slug ?? ''}
              type={(workspace?.type as 'personal' | 'business') ?? 'business'}
              role={workspace?.role ?? 'viewer'}
              id={workspace?.id}
              avatarUrl={(workspace as { avatarUrl?: string | null } | null)?.avatarUrl ?? null}
              status={workspace?.status}
              onEditWorkspace={() => openCentral('/settings/workspace')}
              onTransferOwnership={
                hasWorkspacePermission('transferOwnership')
                  ? () => openCentral('/settings/workspace/transfer')
                  : undefined
              }
              onDeleteWorkspace={
                hasWorkspacePermission('transferOwnership')
                  ? () => openCentral('/settings/workspace/close')
                  : undefined
              }
            />
          </div>
        );
      }

      case SETTINGS_TAB_IDS.MEMBERS:
        return (
          <MembersTabContent
            workspaceId={workspace?.id ?? ''}
            currentUserId={user?.id ?? ''}
            currentRole={(workspace?.role as CentralRole) ?? 'viewer'}
            canManageMembers={hasWorkspacePermission('manageMembers')}
          />
        );

      case SETTINGS_TAB_IDS.BILLING_CREDITS:
        return (
          <BillingTabContent canManageBilling={hasWorkspacePermission('manageBilling')} />
        );

      case SETTINGS_TAB_IDS.NOTIFICATIONS:
        return <NotificationPreferencesTab />;

      default:
        return null;
    }
  };

  // Hide tabs the user cannot meaningfully interact with. Shell renders all core tabs by default;
  // we only extend with extra tabs when we add app-specific ones (none for Legacy today).
  const extraTabs: SettingsTabConfig[] = useMemo(() => [], []);

  return (
    <SettingsShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      extraTabs={extraTabs}
      title="Settings"
    >
      {renderTab()}
    </SettingsShell>
  );
}

// =========================================================================
// Billing tab
// =========================================================================

function BillingTabContent({ canManageBilling }: { canManageBilling: boolean }) {
  const bootContext = useBootContext();
  const workspaceId = bootContext?.currentWorkspace?.id ?? null;
  const toast = useToast();

  const { wallet, isLoading: balanceLoading, isError: balanceError } = useCreditBalance();
  const { subscription, isLoading: subLoading } = useSubscriptionStatus();
  const { isStripe, checkoutEnabled, portalEnabled } = useBillingProvider();

  const [portalLoading, setPortalLoading] = useState(false);

  const billingState: BillingState = useMemo(() => {
    const planCode = subscription?.planCode ?? 'free';
    const planName = planCode.charAt(0).toUpperCase() + planCode.slice(1);
    return {
      plan: {
        planCode,
        planName,
        status: (subscription?.status as BillingState['plan']['status']) ?? 'active',
        currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
        cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd ?? false,
        trialEndsAt: subscription?.trialEndsAt ?? null,
      },
      credits: {
        balance: wallet?.balance ?? 0,
        lowBalanceThreshold: undefined,
      },
      billing: {
        available: canManageBilling && isStripe && portalEnabled,
        provider: isStripe ? ('stripe' as const) : ('none' as const),
      },
      upgradeAvailable: canManageBilling && checkoutEnabled && subscription?.status !== 'canceled',
    };
  }, [subscription, wallet, canManageBilling, isStripe, portalEnabled, checkoutEnabled]);

  const openPortal = useCallback(async () => {
    if (!workspaceId) return;
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession(workspaceId);
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open billing portal');
      setPortalLoading(false);
    }
  }, [workspaceId, toast]);

  // Add Credits / Upgrade both route through the Stripe portal so the user can
  // self-serve plan changes and credit purchases — Central owns plan selection.
  const openCheckoutOrPortal = openPortal;

  return (
    <BillingSection
      billingState={billingState}
      onManageBilling={openPortal}
      onAddCredits={openCheckoutOrPortal}
      onUpgrade={openCheckoutOrPortal}
      isLoading={balanceLoading || subLoading || portalLoading}
      error={balanceError ? 'Unable to load billing data from Central' : null}
    />
  );
}

// =========================================================================
// Members tab
// =========================================================================

function MembersTabContent({
  workspaceId,
  currentUserId,
  currentRole,
  canManageMembers,
}: {
  workspaceId: string;
  currentUserId: string;
  currentRole: CentralRole;
  canManageMembers: boolean;
}) {
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [invitesError, setInvitesError] = useState<string | null>(null);

  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Central-driven invite role options (core roles + app-specific personas).
  // Central returns templates for every app in the workspace (CRM, Sales, etc.);
  // we filter to Legacy core roles + Legacy templates so the invite modal
  // doesn't offer irrelevant cross-app variants.
  const [inviteRoleOptions, setInviteRoleOptions] = useState<InviteRoleOption[] | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!workspaceId || !canManageMembers) return;
    let cancelled = false;
    fetchInviteOptions(workspaceId)
      .then((opts) => {
        if (cancelled) return;
        const legacyOnly = opts.filter((opt) => {
          // Always include core Central roles (Owner / Admin / Member / Viewer).
          if (opt.group === 'core') return true;
          // Only keep template roles that belong to Legacy. Central doesn't
          // expose appId directly on the option, so match on templateId or
          // the displayLabel — both conventionally carry the app name.
          const idMatch = /(^|\W)(legacy|levelup-legacy)/i.test(opt.templateId ?? '');
          const labelMatch = /legacy/i.test(opt.displayLabel);
          return idMatch || labelMatch;
        });
        setInviteRoleOptions(legacyOnly);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = Central deployment hasn't shipped /invite-options yet;
        // MembershipSection will fall back to permissions.selectableRoles.
        if (!(err instanceof InviteOptionsError) || err.status !== 404) {
          console.warn('[invite-options] Failed to fetch from Central:', err);
        }
        setInviteRoleOptions(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, canManageMembers]);

  // Derive selectable core roles from Central's options when available, else
  // fall back to the local hierarchy helper. Central always wins.
  const assignable = useMemo(() => {
    if (inviteRoleOptions && inviteRoleOptions.length > 0) {
      const unique = new Set<CentralRole>();
      for (const opt of inviteRoleOptions) unique.add(opt.coreRole);
      return Array.from(unique);
    }
    return rolesSelectableBy(currentRole);
  }, [inviteRoleOptions, currentRole]);

  const permissions: MembershipPermissions = useMemo(
    () => ({
      canInvite: canManageMembers && assignable.length > 0,
      canRemoveMembers: canManageMembers,
      canChangeRoles: canManageMembers,
      canCancelInvites: canManageMembers,
      selectableRoles: assignable,
      assignableRoles: assignable,
    }),
    [canManageMembers, assignable],
  );

  const fetchMembers = useCallback(async () => {
    if (!workspaceId) {
      setMembersLoading(false);
      return;
    }
    setMembersLoading(true);
    setMembersError(null);
    try {
      setMembers(await listMembers(workspaceId));
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [workspaceId]);

  const fetchInvites = useCallback(async () => {
    if (!workspaceId || !canManageMembers) {
      setInvitesLoading(false);
      return;
    }
    setInvitesLoading(true);
    setInvitesError(null);
    try {
      setInvites(await listPendingInvites(workspaceId));
    } catch (err) {
      setInvitesError(err instanceof Error ? err.message : 'Failed to load invites');
    } finally {
      setInvitesLoading(false);
    }
  }, [workspaceId, canManageMembers]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleInvite = useCallback(
    async (params: { email: string; role: CentralRole; templateId?: string; message?: string }) => {
      if (!workspaceId) return;
      setInviteSubmitting(true);
      setInviteError(null);
      try {
        await inviteMember(workspaceId, {
          email: params.email,
          role: params.role,
          message: params.message,
        });
        toast.success('Invite sent');
        fetchInvites();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invite failed';
        setInviteError(message);
        throw err;
      } finally {
        setInviteSubmitting(false);
      }
    },
    [workspaceId, toast, fetchInvites],
  );

  const handleUpdateRole = useCallback(
    async (userId: string, newRole: CentralRole) => {
      await updateMemberRole(workspaceId, userId, newRole);
      await fetchMembers();
      // If the current user's own role changed, refresh boot context so capabilities update.
      if (userId === currentUserId) {
        const result = await fetchSwitchContext();
        if (result.type === 'success') {
          dispatch(
            bootSuccess({
              user: result.data.user,
              currentWorkspace: result.data.currentWorkspace,
              otherWorkspaces: result.data.otherWorkspaces,
              apps: result.data.apps,
              entitlementsEffective: result.data.entitlementsEffective ?? [],
              meta: result.data.meta ?? null,
            }),
          );
        }
      }
    },
    [workspaceId, currentUserId, fetchMembers, dispatch],
  );

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      await removeMember(workspaceId, userId);
      await fetchMembers();
    },
    [workspaceId, fetchMembers],
  );

  const handleResendInvite = useCallback(
    async (inviteId: string) => {
      await resendInvite(workspaceId, inviteId);
      toast.success('Invite resent');
    },
    [workspaceId, toast],
  );

  const handleCancelInvite = useCallback(
    async (inviteId: string) => {
      await cancelInvite(workspaceId, inviteId);
      await fetchInvites();
    },
    [workspaceId, fetchInvites],
  );

  return (
    <MembershipSection
      members={members}
      invites={invites}
      permissions={permissions}
      currentUserId={currentUserId}
      isLoadingMembers={membersLoading}
      isLoadingInvites={invitesLoading}
      membersError={membersError}
      invitesError={invitesError}
      onInvite={handleInvite}
      onUpdateRole={canManageMembers ? handleUpdateRole : undefined}
      onRemoveMember={canManageMembers ? handleRemoveMember : undefined}
      onResendInvite={canManageMembers ? handleResendInvite : undefined}
      onCancelInvite={canManageMembers ? handleCancelInvite : undefined}
      isInviteSubmitting={inviteSubmitting}
      inviteError={inviteError}
      inviteRoleOptions={inviteRoleOptions}
    />
  );
}

// =========================================================================
// Notification preferences tab
// =========================================================================

function NotificationPreferencesTab() {
  const [preferences, setPreferences] = useState<CategoryPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchNotificationPreferences()
      .then((prefs) => {
        if (!cancelled) setPreferences(prefs);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load preferences');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUpdateCategory = useCallback(
    async (category: string, channel: string, enabled: boolean) => {
      try {
        const updated = await updateNotificationCategory(category, channel, enabled);
        setPreferences((prev) => prev.map((p) => (p.category === category ? updated : p)));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update preference');
      }
    },
    [],
  );

  return (
    <NotificationPreferencesSection
      preferences={preferences}
      onUpdateCategory={handleUpdateCategory}
      isLoading={loading}
      error={error}
    />
  );
}
